import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBarConfig } from '@angular/material/snack-bar';
import { Currency, MerchantService } from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

interface DialogData {
  merchantId: string;
  invoiceId: string;
  outstandingBalance: number;
  totalAmount: number;
  invoiceNumber: number;
  invoiceCurrency: Currency;
}

@Component({
  selector: 'billing-ui-charge-invoice-dialog',
  templateUrl: './charge-invoice-dialog.component.html',
  styleUrls: ['./charge-invoice-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ChargeInvoiceDialogComponent {
  loading$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  merchantId: string;
  invoiceId: string;
  outstandingBalance: number;
  totalAmount: number;
  invoiceNumber: number;
  invoiceCurrency: Currency;
  formGroup: UntypedFormGroup;

  constructor(
    public dialogRef: MatDialogRef<ChargeInvoiceDialogComponent>,
    private merchantService: MerchantService,
    private alertService: SnackbarService,
    private formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
  ) {
    this.merchantId = data.merchantId;
    this.invoiceId = data.invoiceId;
    this.outstandingBalance = data.outstandingBalance;
    this.totalAmount = data.totalAmount;
    this.invoiceNumber = data.invoiceNumber;
    this.invoiceCurrency = data.invoiceCurrency;
    this.formGroup = this.formBuilder.group({
      amount: new UntypedFormControl(null, [Validators.required, Validators.min(0.5)]),
    });
  }

  public onSave(): void {
    this.loading$$.next(true);
    const { amount } = this.formGroup.getRawValue();
    const cleanedAmount = amount.replace(/,/g, '');
    const SNACKBAR_CONFIG = { duration: 45 * 1000, panelClass: [] } as MatSnackBarConfig;
    this.merchantService
      .chargeSalesInvoice(this.merchantId, this.invoiceId, Number((parseFloat(cleanedAmount) * 100).toFixed(0)))
      .pipe(
        tap(() => {
          const displayAmount = new CurrencyPipe('en-US').transform(
            cleanedAmount,
            this.invoiceCurrency,
            'symbol',
            '1.2-2',
          );
          this.alertService.openWithOptions(`Successfully charged invoice ${displayAmount}`, {
            duration: SNACKBAR_CONFIG.duration,
            action: 'OK',
          });
          this.dialogRef.close();
        }),
        catchError((err) => {
          const errorMessage = err.error && err.error.message ? err.error.message : `Failed to charge invoice.`;
          this.alertService.openWithOptions(errorMessage, { duration: SNACKBAR_CONFIG.duration, action: 'Dismiss' });
          this.dialogRef.close();
          throw err;
        }),
        finalize(() => this.loading$$.next(false)),
      )
      .subscribe();
  }
}
